import { supabase } from "@/integrations/supabase/client";
import { Profile } from "./types";
import { getProfileFromCache, updateProfileCache } from "./profileCache";
import { createDefaultProfile } from "./createDefaultProfile";
import { createLogger } from "../utils/loggingUtils";
import { toast } from "sonner";

const logger = createLogger('fetchProfile');

export const fetchProfile = async (userId: string): Promise<Profile | null> => {
  try {
    // Check cache first
    const cachedProfile = getProfileFromCache(userId);
    if (cachedProfile) {
      logger.info("Using cached profile for user:", userId);
      return cachedProfile;
    }
    
    logger.info("Fetching profile for user:", userId);
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      logger.error("Error fetching profile:", error);
      
      // If the error is that the profile doesn't exist, we should create a default one
      if (error.code === 'PGRST116') { // Code for "no rows returned"
        logger.info("Profile not found, creating default profile");
        const defaultProfile = await createDefaultProfile(userId);
        if (defaultProfile) {
          return defaultProfile;
        }
        
        // If we can't create a default profile, log and return null
        logger.warn("Failed to create default profile");
        return null;
      }
      
      // If auth error, try refreshing session once
      if (error.message.includes('JWT') || error.code === 'PGRST301') {
        logger.warn("JWT error fetching profile, attempting to refresh session");
        
        try {
          await supabase.auth.refreshSession();
          
          // Retry after session refresh
          const { data: retryData, error: retryError } = await supabase
            .from("profiles")
            .select("*")
            .eq("id", userId)
            .single();
            
          if (retryError) {
            logger.error("Error fetching profile after session refresh:", retryError);
            return null;
          }
          
          // Update cache with the fetched profile
          updateProfileCache(userId, retryData as Profile);
          logger.info("Successfully fetched profile after session refresh:", retryData);
          return retryData as Profile;
        } catch (refreshError) {
          logger.error("Error refreshing session:", refreshError);
          return null;
        }
      }
      
      return null;
    }

    // SECURITY: Removed hardcoded admin email logic - admin privileges must be granted through proper channels

    // Update cache with the fetched profile
    updateProfileCache(userId, data as Profile);
    
    logger.info("Successfully fetched profile:", data);
    return data as Profile;
  } catch (error) {
    logger.error("Exception fetching profile:", error);
    
    // In case of exception, try to return cached data as fallback
    const cachedProfile = getProfileFromCache(userId);
    if (cachedProfile) {
      logger.info("Returning cached profile after error");
      return cachedProfile;
    }
    
    return null;
  }
};